import { memo, useEffect, useRef, useState, useCallback } from "react";
import "./GlobalChat.scss";
import { Spin, Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Link } from "react-router-dom";
import { getLocalStorageData } from "../../../shared/commonFunction";
import sendIcon from "../../../assets/icons/send-icon.svg";
import useSocket from "../../../hooks/useSocket";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import fire from "../../../assets/icons/fire.svg";
import { ArrowLeftOutlined, LoadingOutlined } from "@ant-design/icons";

const GlobalChat = ({ setChatIsVisible, isMobile }) => {
  const { t } = useTranslation("common");
  const [allMessages, setAllMessages] = useState([]);
  const [message, setMessage] = useState({
    text: "",
    imageUrl: "",
    videoUrl: "",
  });
  const [loading, setLoading] = useState(true);
  const [todayDate] = useState(moment().format("D MMM"));

  const chatEndRef = useRef(null);
  const socketConnection = useSocket();
  const { socket, isReady } = socketConnection;

  const userData =
    useSelector((state) => state.user.userData) ||
    JSON.parse(getLocalStorageData("user"));
  const isLogin = useSelector((state) => state.login.isLogin);

  const handleGlobalMessage = useCallback((data) => {
    console.log('data: ', data);
    setLoading(false);
    setAllMessages(data?.messages || []);
  }, []);

  useEffect(() => {
    if (!socket || !isReady) return;
    console.log("global message called")
    socket.emit("global-message-page", {
      data: "callled"
    });
    socket.emit("global-message-page", {})
    socket.on("globalMessages", handleGlobalMessage);

    return () => {
      socket.off("globalMessages", handleGlobalMessage);
    };
  }, [socket, isReady, handleGlobalMessage]);


  const handleOnChange = useCallback((e) => {
    setMessage((prev) => ({ ...prev, text: e.target.value }));
  }, []);

  const handleSendMessage = useCallback(
    (e) => {
      e.preventDefault();
      if (!message.text && !message.imageUrl && !message.videoUrl) return;
      if (!socket) return;

      socket.emit("new-global-message", {
        text: message.text,
        imageUrl: message.imageUrl,
        videoUrl: message.videoUrl,
        msgByUserId: userData?._id,
      });

      setMessage({ text: "", imageUrl: "", videoUrl: "" });
    },
    [message, socket, userData?._id]
  );

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage(e);
      }
    },
    [handleSendMessage]
  );

  const renderDate = useCallback(
    (message, index) => {
      if (
        index === 0 ||
        moment(allMessages[index - 1]?.createdAt).format("LL") !==
        moment(message?.createdAt).format("LL")
      ) {
        return (
          <>
            <div className="msg-divide-hr"></div>
            <div key={`date-${index}`} className="message-date">
              {moment(message?.createdAt).format("D MMM") === todayDate
                ? "Today"
                : moment(message?.createdAt).format("D MMM, h:mmA")}
            </div>
          </>
        );
      }
      return null;
    },
    [allMessages, todayDate]
  );

  useEffect(() => {
    chatEndRef.current?.scrollIntoViewIfNeeded({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  }, [allMessages]);

  return (
    <div className="global-chat-box">
      <Typography.Title level={5} className="chat-header">
        {!isMobile && isLogin && (
          <ArrowLeftOutlined
            onClick={() => setChatIsVisible(false)}
            className="chat-back-icon"
          />
        )}
        {t("one_to_one.global_chat")}
      </Typography.Title>

      <div className="global-message-container">
        {loading && <Spin indicator={<LoadingOutlined spin />} className="global-spin" size="large" />}

        {console.log('allMessages: ', allMessages)}
        {allMessages?.map((msg, index) => (
          <div key={index}>
            {msg.messageType === "chat" ? (
              <div className="particular-user-chat">
                {renderDate(msg, index)}
                <div
                  className={`global-message-bubble ${userData._id === msg?.msgByUserId?._id ? "received" : "sent"
                    }`}
                >
                  <div
                    className="one-to-one-message"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {userData._id !== msg?.msgByUserId?._id && (
                      <Link
                        to={
                          msg.userReference === "User" &&
                          `/consumer-Profile/${msg?.msgByUserId?._id}`
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <img
                          src={(msg?.msgByUserId?.avatarUrl && msg?.msgByUserId?.avatarUrl != 'null') ? msg?.msgByUserId?.avatarUrl : "https://fapi.fanzaty.net/api/v1/avtar/get-image/boy3.svg"}
                          alt="chat-user-img"
                          className="received"
                          loading="lazy"
                        />
                      </Link>
                    )}

                    <div style={{ padding: "5px" }}>
                      {msg.text && (
                        <>
                          {msg?.msgByUserId?._id !== userData?._id && (
                            <Link
                              to={
                                msg.userReference === "User" &&
                                `/consumer-Profile/${msg?.msgByUserId?._id}`
                              }
                            >
                              <p
                                className="message-inner-user-name"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                {msg?.msgByUserId?.userName}
                                {msg?.msgByUserId?.winStreak > 0 && (
                                  <>
                                    <Tooltip title={t("one_to_one.win_streak")}><img
                                      src={fire}
                                      alt="fire"
                                      className="chat-fire-img"
                                      loading="lazy"
                                    />
                                    </Tooltip>
                                    <span>{msg?.msgByUserId?.winStreak}</span>
                                  </>
                                )}
                              </p>
                            </Link>
                          )}

                          <div
                            className={
                              userData?._id !== msg?.msgByUserId?._id
                                ? "message-text-sent"
                                : "message-text-received"
                            }
                          >
                            {msg.text.split("\n").map((chunk, idx) => (
                              <p key={idx}>{chunk}</p>
                            ))}
                          </div>
                        </>
                      )}

                      {msg.text && (
                        <p className="message-timestamp">
                          {moment(msg.createdAt).format("h:mmA")}
                        </p>
                      )}
                      <div ref={chatEndRef}></div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {renderDate(msg, index)}
                <div className="invite-message">{msg.text}</div>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="global-type-message-section">
        <div className="global-input-send">
          <div className="global-chat-input">
            <textarea
              name="text"
              id="text"
              value={message.text}
              onChange={handleOnChange}
              onKeyDown={handleKeyPress}
              className="global-msg-input"
              placeholder={t("chatMessages.placeholder")}
            />
          </div>
          <div>
            <img
              src={sendIcon}
              alt="loading-select-file"
              className="global-send-icon-image"
              onClick={handleSendMessage}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

GlobalChat.propTypes = {
  setChatIsVisible: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
};

export default memo(GlobalChat);
